import { Button } from "@/atoms-react/button/Button.react";
import Column from "@/atoms-react/column/Column.react";
import Image from "@/atoms-react/image/Image.react";
import InputLabel from "@/atoms-react/input/Input.react";
import { useSecurity } from "@/features/security/hooks/use-react-security";
import { ChevronRightIcon } from "@heroicons/react/20/solid";
import _ from "lodash";
import type React from "react";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { FlowHeader } from "../../../flow-header.react";
import { A } from "@/atoms-react/a/A.react";
import { isSystemThemeDark } from "@/features/utils/theme.ts";
import Loader from "@/atoms-react/loader/Loader.react";

export type AccountSecurityWebauthnOutput = {
  valid: boolean;
};

type AccountSecurityWebauthnProps = {
  onValidated: (output: AccountSecurityWebauthnOutput) => void;
  force_webauthn?: Boolean;
} & React.ComponentPropsWithoutRef<"div">;

export const AccountSecurityWebauthn = ({
  onValidated = () => {},
  force_webauthn = false,
  ...props
}: AccountSecurityWebauthnProps) => {
  const { t } = useTranslation(["pass"]);
  const [isDark, setIsDark] = useState<boolean>(false);
  const [loading, SetLoading] = useState<boolean>(false);
  const [loadingExecute, SetLoadingExecute] = useState<boolean>(false);
  const { getIsWebAuthnActivated, newWebauthn } = useSecurity();

  const { refetch } = getIsWebAuthnActivated({
    enabled: false,
    queryKey: ["security", "isWebAuthnActivated"]
  });

  useEffect(() => {
    const prefersDarkMode = isSystemThemeDark();
    setIsDark(prefersDarkMode);
  }, []);

  const { mutateAsync: createNewWebauthn } = newWebauthn();
  const autoDeviceName = navigator.userAgent.includes("iPhone")
    ? "iPhone"
    : navigator.userAgent.includes("Android")
      ? "Android"
      : navigator.userAgent.includes("Mac")
        ? "Mac"
        : navigator.userAgent.includes("Windows")
          ? "Windows"
          : "No name";
  const [deviceName, setDeviceName] = useState<string>("TODO_CHANGE_ME");

  const execute = async () => {
    SetLoadingExecute(true);
    if (deviceName) {
      const challenge = await createNewWebauthn(deviceName);
      if (challenge) {
        sessionStorage.removeItem("force_webauthn");
        toast.success(t("flow.id_control.success.account_protected"));
        await refetch();
        onValidated({ valid: true });
      } else {
        toast.error(t("flow.id_control.error.webauthn_failed"));
        onValidated({ valid: false });
        SetLoadingExecute(false);
      }
    }
  };

  return (
    <Column {..._.omit(props, "key")}>
      <div className="mb-12 grow">
        <FlowHeader
          title={t("flow.id_control.signup.label.account_security_title_webauthn")}
          description={t("flow.id_control.signup.label.account_security_description_webauthn")}
          back={false}
        />

        {!autoDeviceName && (
          <InputLabel
            autoFocus
            placeholder={t("flow.id_control.webauthn.device_name")}
            className={"mb-8"}
            value={deviceName}
            onChange={(value: string) => setDeviceName(value)}
            label={t("flow.id_control.webauthn.device_name")}
          />
        )}

        <div className={"flex w-full items-center justify-center"}>
          <Image
            src={isDark ? "/images/olkypass/touch-id-dark.svg" : "/images/olkypass/touch-id.svg"}
            className={"w-1/2"}
            alt="Touch ID"
          />
        </div>
      </div>

      <Button
        shortcut="enter"
        className="shrink-0"
        onClick={() => execute()}
        icon={<ChevronRightIcon />}
        loading={loadingExecute}
      >
        {t("flow.id_control.input.continue")}
      </Button>
      <div className="my-4 flex items-center justify-center text-primary">
        {!force_webauthn && (
          <A
            onClick={() => {
              onValidated({ valid: false });
              SetLoading(true);
            }}
          >
            {loading ? <Loader /> : t("flow.id_control.input.skip")}
          </A>
        )}
      </div>
    </Column>
  );
};
